import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePreviousValue } from '@wix/da-hooks/pkg/usePreviousValue';
import { AppState } from '../../types/store';
import { getModuleRequestsPath } from '../../client/selectors/modules';
import {
  fetchModuleEndpoint,
  fetchModuleEndpointPurgeData,
} from '../../client/actions/modules';

export function useModuleEndpoint(moduleId: number, path: string = '/') {
  const dispatch = useDispatch();

  const {
    fetchedPath,
    response,
    isFetching,
    errorDescription: error,
  } = useSelector((state: AppState) =>
    getModuleRequestsPath(state, moduleId, path)
  );

  const wasFetching = usePreviousValue(isFetching);
  const hasJustFinishedFetching = wasFetching && !isFetching;
  const moduleVersion = response?.config?.version;

  const reset = useCallback(() => {
    dispatch(
      fetchModuleEndpointPurgeData({
        id: moduleId,
        path,
      })
    );
  }, [dispatch, moduleId, path]);

  const fetchEndpoint = useCallback(
    ({ data, method = 'get' }) => {
      if (!isFetching) {
        dispatch(
          fetchModuleEndpoint({
            ...data,
            id: moduleId,
            path,
            method,
            version: moduleVersion,
          })
        );
      }
    },
    [isFetching, moduleVersion, dispatch, moduleId, path]
  );

  const getEndpoint = useCallback(
    (data = {}) => fetchEndpoint({ data, method: 'get' }),
    [fetchEndpoint]
  );

  const postEndpoint = useCallback(
    (data = {}) => fetchEndpoint({ data, method: 'post' }),
    [fetchEndpoint]
  );

  return {
    getEndpoint,
    postEndpoint,
    fetchEndpoint,
    isFetching,
    hasJustFinishedFetching,
    fetchedPath,
    response,
    error,
    reset,
  };
}

export default useModuleEndpoint;
